<template>
  <div class="review-dingpan p-2 bg-main">
    <StockInfoSidebar :symbol_code="sidebar_symbol_code" :symbol_name="sidebar_symbol_name" :date_mark="sidebar_date_mark"></StockInfoSidebar>
    <div class="dingpan-toolbar d-flex align-items-center flex-wrap mb-2">
      <div class="d-flex align-items-center mr-2">
        <b-form-datepicker
          size="sm"
          v-model="date1"
          :date-disabled-fn="dateDisabled"
          @context="onDatePicked"
          today-button
          close-button
          locale="zh"
        ></b-form-datepicker>
      </div>
      <b-button size="sm" variant="outline-primary" class="border-0" @click="fetchData">
        <b-icon icon="arrow-clockwise"></b-icon>
      </b-button>
      <div class="d-flex align-items-center ml-2">
        <b-form-select v-model="show_top_n" :options="topNOptions" size="sm"></b-form-select>
      </div>
      <div class="d-flex align-items-center ml-2">
        <b-form-checkbox
          id="checkbox_show_signal_only"
          v-model.number="show_signal_only"
          value="1"
          unchecked-value="0"
          size="sm"
        >信号</b-form-checkbox>
      </div>
      <div class="d-flex align-items-center ml-2">
        <b-form-checkbox
          id="checkbox_show_volume_increase_only"
          v-model.number="show_volume_increase_only"
          value="1"
          unchecked-value="0"
          size="sm"
        >放量</b-form-checkbox>
      </div>
      <span v-if="tradeDays.length" class="text-muted small ml-2">
        <span class="badge badge-secondary mr-1" v-for="day in tradeDays" :key="day">{{ formatDayLabel(day) }}</span>
      </span>
    </div>

    <div v-if="loading" class="text-center p-4">
      <b-spinner small variant="primary"></b-spinner>
    </div>
    <div v-else>
      <div v-if="tradeDays.length === 0" class="text-center text-muted p-4">暂无数据</div>
      <div v-else>
        <div v-if="!isMobile" class="mb-3">
          <div class="font-weight-bold mb-2">梯队</div>
          <div class="dingpan-grid-wrap">
            <div class="dingpan-grid" :style="{ minWidth: plateList.length * 200 + 80 + 'px' }">
              <div class="row no-gutters dingpan-grid-header">
                <div class="border alert-primary dingpan-left text-center d-flex align-items-center justify-content-center" style="width: 80px; flex: 0 0 80px;">梯队/题材</div>
                <div
                  v-for="plate in plateList"
                  :key="plate.plate_code"
                  class="col border alert-danger text-truncate dingpan-plate text-center font-weight-bold p-1"
                >
                  {{ plate.plate_name }}
                </div>
              </div>
              <div
                v-for="level in ladder"
                :key="level.level"
                class="row no-gutters dingpan-grid-row"
              >
                <div class="border alert-danger dingpan-left text-center d-flex flex-column align-items-center justify-content-center" style="width: 80px; flex: 0 0 80px;">
                  <div class="font-weight-bold">{{ formatLevel(level.level) }}</div>
                  <div class="small text-muted">共{{ (level.stocks || []).length }}只</div>
                </div>
                <div
                  v-for="plate in plateList"
                  :key="plate.plate_code + '_' + level.level"
                  class="col border p-0"
                >
                  <div
                    v-for="stock in (stocksGrid[level.level] && stocksGrid[level.level][plate.plate_code]) || []"
                    :key="stock.stock_code + '_' + plate.plate_code"
                    class="auction-stock p-1 border-bottom"
                  >
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <div>
                        <span class="font-weight-bold text-truncate mr-2" style="cursor: pointer;" @click="showSidebar(stock)">{{ stock.stock_name || stock.stock_code }}</span>
                        <span class="small text-muted mr-1">{{ stock.stock_code }}</span>
                      </div>
                      <div v-if="stock.failed" class="badge badge-warning">晋级失败</div>
                    </div>
                    
                    <table class="table table-bordered table-sm mb-0 text-center stock-table">
                      <thead>
                        <tr class="text-muted">
                          <th style="width: 20px" title="信号">信</th>
                          <th style="width: 20px" title="日期">日</th>
                          <th title="涨停委买额(亿)">委</th>
                          <th title="竞价涨幅(%)">涨</th>
                          <th title="竞价金额(亿)">额</th>
                          <th title="竞价净额(亿)">净</th>
                          <th title="天成交额(亿)">天额</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(day, index) in tradeDays" :key="day">
                          <td :class="{'bg-signal-active': getSignalValue(stock, index) != 0}">{{ getSignalValue(stock, index) }}</td>
                          <td>{{ formatDayNum(day) }}</td>
                          <td :class="[getTrendClass(stock, index, 'up_limit_buy_amount'), getTrendBackgroundClass(stock, index, 'up_limit_buy_amount')]">{{ formatAmount(getAuctionValue(stock, day, 'up_limit_buy_amount')) }}</td>
                          <td :class="[getPctClass(getAuctionValue(stock, day, 'auction_pct')), getTrendBackgroundClass(stock, index, 'auction_pct')]">{{ formatPct(getAuctionValue(stock, day, 'auction_pct')) }}</td>
                          <td :class="[getTrendClass(stock, index, 'auction_amount'), getTrendBackgroundClass(stock, index, 'auction_amount')]">{{ formatAmount(getAuctionValue(stock, day, 'auction_amount')) }}</td>
                          <td>{{ formatAmount(getAuctionValue(stock, day, 'auction_net_amount')) }}</td>
                          <td :class="[getTrendClass(stock, index, 'daily_trade_amount'), getVolumeTrendBackgroundClass(stock, index, 'daily_trade_amount')]">{{ formatAmount(getAuctionValue(stock, day, 'daily_trade_amount')) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="mb-3">
          <div class="font-weight-bold mb-2">梯队</div>
          <div v-for="level in filteredLadder" :key="level.level" class="mb-3">
            <div class="d-flex align-items-center mb-1">
              <span class="badge badge-danger mr-2">{{ formatLevel(level.level) }}</span>
              <span class="text-muted small">共{{ (level.stocks || []).length }}只</span>
            </div>
            <b-table
              :items="level.stocks"
              :fields="stockFields"
              striped
              hover
              small
              responsive="sm"
              class="mb-0 border shadow-sm rounded"
            >
              <template #cell(signal)="data">
                <div v-for="(day, index) in tradeDays" :key="day" class="small d-flex justify-content-between">
                  <span class="text-muted">{{ formatDayLabel(day) }}</span>
                  <span :class="{'bg-signal-active': getSignalValue(data.item, index) != 0, 'px-1': getSignalValue(data.item, index) != 0}">{{ getSignalValue(data.item, index) }}</span>
                </div>
              </template>
              <template #cell(stock)="data">
                <div class="font-weight-bold" style="white-space: nowrap; cursor: pointer;" @click="showSidebar(data.item)">
                  {{ data.item.stock_name || data.item.stock_code }}
                  <span v-if="data.item.failed" class="badge badge-warning ml-1">失败</span>
                </div>
                <div class="small text-secondary">
                  <span class="mr-1">{{ data.item.stock_code }}</span>
                </div>
              </template>
              <template #cell(plates)="data">
                <div v-if="(data.item.plates || []).length === 0" class="text-muted small">--</div>
                <div v-else>
                  <span
                    v-for="plate in data.item.plates"
                    :key="plate.plate_code"
                    class="badge badge-light mr-1 mb-1"
                  >{{ plate.plate_name }}</span>
                </div>
              </template>
              <template #cell(up_limit_buy_amount)="data">
                <div v-for="(day, index) in tradeDays" :key="day" class="small d-flex justify-content-between">
                  <span class="text-muted">{{ formatDayLabel(day) }}</span>
                  <span :class="[getTrendClass(data.item, index, 'up_limit_buy_amount'), getTrendBackgroundClass(data.item, index, 'up_limit_buy_amount')]">{{ formatAmount(getAuctionValue(data.item, day, 'up_limit_buy_amount')) }}</span>
                </div>
              </template>
              <template #cell(auction_pct)="data">
                <div v-for="day in tradeDays" :key="day" class="small d-flex justify-content-between">
                  <span class="text-muted">{{ formatDayLabel(day) }}</span>
                  <span :class="getPctClass(getAuctionValue(data.item, day, 'auction_pct'))">
                    {{ formatPct(getAuctionValue(data.item, day, 'auction_pct')) }}
                  </span>
                </div>
              </template>
              <template #cell(auction_amount)="data">
                <div v-for="(day, index) in tradeDays" :key="day" class="small d-flex justify-content-between">
                  <span class="text-muted">{{ formatDayLabel(day) }}</span>
                  <span :class="[getTrendClass(data.item, index, 'auction_amount'), getTrendBackgroundClass(data.item, index, 'auction_amount')]">{{ formatAmount(getAuctionValue(data.item, day, 'auction_amount')) }}</span>
                </div>
              </template>
              <template #cell(auction_net_amount)="data">
                <div v-for="day in tradeDays" :key="day" class="small d-flex justify-content-between">
                  <span class="text-muted">{{ formatDayLabel(day) }}</span>
                  <span>{{ formatAmount(getAuctionValue(data.item, day, 'auction_net_amount')) }}</span>
                </div>
              </template>
              <template #cell(auction_turnover)="data">
                <div v-for="day in tradeDays" :key="day" class="small d-flex justify-content-between">
                  <span class="text-muted">{{ formatDayLabel(day) }}</span>
                  <span>{{ formatNumber(getAuctionValue(data.item, day, 'auction_turnover')) }}</span>
                </div>
              </template>
            </b-table>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import moment from 'moment'
import StockInfoSidebar from 'components/stock/StockInfoSidebar.vue'

export default {
  components: {
    StockInfoSidebar
  },
  data() {
    return {
      date1: moment().format('YYYY-MM-DD'),
      windowWidth: window.innerWidth,
      tradeDays: [],
      ladder: [],
      themes: [],
      loading: false,
      show_top_n: 12,
      show_signal_only: 0,
      show_volume_increase_only: 0,
      sidebar_symbol_code: '',
      sidebar_symbol_name: '',
      sidebar_date_mark: '',
      topNOptions: [
        { value: 5, text: '显示板块: 5个' },
        { value: 8, text: '显示板块: 8个' },
        { value: 10, text: '显示板块: 10个' },
        { value: 12, text: '显示板块: 12个' },
        { value: 15, text: '显示板块: 15个' },
        { value: 20, text: '显示板块: 20个' }
      ],
      stockFields: [
        { key: 'signal', label: '信', thStyle: { width: '5%' } },
        { key: 'stock', label: '股票', thStyle: { width: '12%' } },
        { key: 'plates', label: '题材', thStyle: { width: '16%' } },
        { key: 'up_limit_buy_amount', label: '委买(亿)', thStyle: { width: '14%' } },
        { key: 'auction_pct', label: '涨幅(%)', thStyle: { width: '12%' } },
        { key: 'auction_amount', label: '金额(亿)', thStyle: { width: '14%' } },
        { key: 'auction_net_amount', label: '净额(亿)', thStyle: { width: '14%' } },
        { key: 'auction_turnover', label: '换手(%)', thStyle: { width: '12%' } }
      ]
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768
    },
    filteredLadder() {
        if (this.show_signal_only !== 1 && this.show_volume_increase_only !== 1) return this.ladder
        
        return this.ladder.map(level => {
            const filteredStocks = (level.stocks || []).filter(stock => {
                let keep = true
                
                if (this.show_signal_only === 1) {
                    let hasSignal = false
                    for (let i = 0; i < this.tradeDays.length; i++) {
                        if (this.getSignalValue(stock, i) != 0) {
                            hasSignal = true
                            break
                        }
                    }
                    if (!hasSignal) keep = false
                }
                
                if (keep && this.show_volume_increase_only === 1) {
                    let hasVolumeIncrease = false
                    for (let i = 0; i < this.tradeDays.length; i++) {
                        if (this.getVolumeTrendBackgroundClass(stock, i, 'daily_trade_amount') === 'bg-trend-blue') {
                            hasVolumeIncrease = true
                            break
                        }
                    }
                    if (!hasVolumeIncrease) keep = false
                }
                
                return keep
            })
            
            return {
                ...level,
                stocks: filteredStocks
            }
        }).filter(level => level.stocks.length > 0)
    },
    plateList() {
      let list = []
      if (this.themes && this.themes.length > 0) {
        list = this.themes.map(item => ({
          plate_code: item.plate_code,
          plate_name: item.plate_name,
          score: item.score
        }))
      } else {
        const map = {}
        this.ladder.forEach(level => {
          (level.stocks || []).forEach(stock => {
            (stock.plates || []).forEach(plate => {
              if (!map[plate.plate_code]) {
                map[plate.plate_code] = {
                  plate_code: plate.plate_code,
                  plate_name: plate.plate_name,
                  score: plate.score
                }
              }
            })
          })
        })
        list = Object.values(map)
      }
      return list.slice(0, this.show_top_n)
    },
    stocksGrid() {
      const grid = {}
      this.ladder.forEach(level => {
        const key = level.level
        grid[key] = {}
        this.plateList.forEach(plate => {
          grid[key][plate.plate_code] = []
        })
        ;(level.stocks || []).forEach(stock => {
          if (this.show_signal_only === 1) {
              // Check if any day has signal != 0
              let hasSignal = false
              for (let i = 0; i < this.tradeDays.length; i++) {
                  if (this.getSignalValue(stock, i) != 0) {
                      hasSignal = true
                      break
                  }
              }
              if (!hasSignal) return
          }
          
          if (this.show_volume_increase_only === 1) {
              let hasVolumeIncrease = false
              for (let i = 0; i < this.tradeDays.length; i++) {
                  if (this.getVolumeTrendBackgroundClass(stock, i, 'daily_trade_amount') === 'bg-trend-blue') {
                      hasVolumeIncrease = true
                      break
                  }
              }
              if (!hasVolumeIncrease) return
          }
          
          const plates = stock.plates || []
          if (plates.length === 0) return
          plates.forEach(plate => {
            if (!grid[key][plate.plate_code]) {
              grid[key][plate.plate_code] = []
            }
            grid[key][plate.plate_code].push(stock)
          })
        })
      })
      return grid
    }
  },
  watch: {
    show_top_n(newVal) {
      localStorage.setItem('auction_show_top_n', newVal)
    },
    show_signal_only(newVal) {
      localStorage.setItem('auction_show_signal_only', newVal)
    },
    show_volume_increase_only(newVal) {
      localStorage.setItem('auction_show_volume_increase_only', newVal)
    }
  },
  mounted() {
    const savedShowTopN = localStorage.getItem('auction_show_top_n')
    if (savedShowTopN !== null) {
      this.show_top_n = parseInt(savedShowTopN)
    }
    const savedShowSignalOnly = localStorage.getItem('auction_show_signal_only')
    if (savedShowSignalOnly !== null) {
      this.show_signal_only = parseInt(savedShowSignalOnly)
    }
    const savedShowVolumeIncreaseOnly = localStorage.getItem('auction_show_volume_increase_only')
    if (savedShowVolumeIncreaseOnly !== null) {
      this.show_volume_increase_only = parseInt(savedShowVolumeIncreaseOnly)
    }

    this.setDefaultTradeDateByRule()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onResize() {
      this.windowWidth = window.innerWidth
    },
    setDefaultTradeDateByRule() {
      const dayEnd = moment().utcOffset(8).add(1, 'days').format('YYYY-MM-DD')
      request_zzquant({
        url: '/v3/market/trade/days',
        params: { days: 2, day_end: dayEnd }
      }).then(res => {
        if (res.data && res.data.code === 20000 && Array.isArray(res.data.data) && res.data.data.length > 0) {
          this.date1 = res.data.data[0]
        } else {
          this.date1 = dayEnd
        }
        this.fetchData()
      }).catch(() => {
        this.date1 = dayEnd
        this.fetchData()
      })
    },
    onDatePicked(ctx) {
      this.date1 = ctx.selectedYMD
      this.fetchData()
    },
    dateDisabled(ymd, date) {
      const weekday = date.getDay()
      if (weekday === 0 || weekday === 6) return true
      const now = new Date()
      if (date.getTime() >= now.getTime()) return true
      return false
    },
    fetchData() {
      if (!this.date1) return
      this.loading = true
      request_zzquant({
        url: '/v3/open/review/dingpan/auction',
        params: { date1: this.date1 }
      }).then(res => {
        this.loading = false
        if (res.data && res.data.code === 20000) {
          const data = res.data.data || {}
          this.tradeDays = (data.trade_days || []).sort().reverse()
          this.ladder = data.ladder || []
          this.themes = data.themes || []
        }
      }).catch(() => {
        this.loading = false
      })
    },
    getAuctionValue(item, day, key) {
      const series = item && item.auction ? item.auction : []
      const record = series.find(row => row.date === day)
      return record ? record[key] : null
    },
    formatDayLabel(day) {
      if (!day) return '--'
      return moment(day).format('MM-DD')
    },
    formatDayNum(day) {
      if (!day) return '--'
      return moment(day).format('DD')
    },
    formatLevel(level) {
      const num = Number(level)
      if (Number.isNaN(num)) return level
      if (num <= 0) return '炸板'
      return num + '板'
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === '') return '--'
      const num = Number(value)
      if (Number.isNaN(num)) return '--'
      return (num / 100000000).toFixed(2)
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '--'
      const num = Number(value)
      if (Number.isNaN(num)) return '--'
      return num.toFixed(2)
    },
    formatPct(value) {
      if (value === null || value === undefined || value === '') return '--'
      const num = Number(value)
      if (Number.isNaN(num)) return '--'
      return num.toFixed(2)
    },
    getPctClass(val) {
      const num = Number(val)
      if (num > 0) return 'text-danger'
      if (num < 0) return 'text-success'
      return 'text-secondary'
    },
    getTrendClass(stock, index, key) {
      if (index + 1 >= this.tradeDays.length) return ''
      
      const currentDay = this.tradeDays[index]
      const nextDay = this.tradeDays[index + 1]
      
      const currentVal = this.getAuctionValue(stock, currentDay, key)
      const nextVal = this.getAuctionValue(stock, nextDay, key)
      
      if (currentVal == null || nextVal == null) return ''
      
      const curr = Number(currentVal)
      const next = Number(nextVal)
      
      if (Number.isNaN(curr) || Number.isNaN(next)) return ''
      
      if (next === 0) {
         if (curr > 0) return 'text-danger font-weight-bold'
         if (curr < 0) return 'text-success font-weight-bold'
         return ''
      }
      
      const rate = (curr - next) / Math.abs(next)
      
      if (rate > 0.5) return 'text-danger font-weight-bold'
      if (rate < -0.4) return 'text-success font-weight-bold'
      
      return ''
    },
    getTrendBackgroundClass(stock, index, key) {
      if (index + 1 >= this.tradeDays.length) return ''
      
      const currentDay = this.tradeDays[index]
      const nextDay = this.tradeDays[index + 1]
      
      const currentVal = this.getAuctionValue(stock, currentDay, key)
      const nextVal = this.getAuctionValue(stock, nextDay, key)
      
      if (currentVal == null || nextVal == null) return ''
      
      const curr = Number(currentVal)
      const next = Number(nextVal)
      
      if (Number.isNaN(curr) || Number.isNaN(next)) return ''
      
      if (next === 0) {
         if (curr > 0) return 'bg-trend-up'
         return ''
      }
      
      const rate = (curr - next) / Math.abs(next)
      
      if (rate > 0.5) return 'bg-trend-up'
      
      return ''
    },
    getVolumeTrendBackgroundClass(stock, index, key) {
      if (index + 1 >= this.tradeDays.length) return ''
      
      const currentDay = this.tradeDays[index]
      const nextDay = this.tradeDays[index + 1]
      
      const currentVal = this.getAuctionValue(stock, currentDay, key)
      const nextVal = this.getAuctionValue(stock, nextDay, key)
      
      if (currentVal == null || nextVal == null) return ''
      
      const curr = Number(currentVal)
      const next = Number(nextVal)
      
      if (Number.isNaN(curr) || Number.isNaN(next)) return ''
      
      if (next === 0) {
         if (curr > 500000000) return 'bg-trend-blue'
         return ''
      }
      
      const rate = (curr - next) / Math.abs(next)
      
      if (rate > 0.5 && curr > 500000000) return 'bg-trend-blue'
      if (rate < -0.5) return 'bg-trend-red'
      
      return ''
    },
    getSignalValue(stock, index) {
      if (index >= this.tradeDays.length) return 0
      
      const day = this.tradeDays[index]
      let signals = []
      
      // Logic for 1
      const isRed = (key) => {
          return this.getTrendBackgroundClass(stock, index, key) === 'bg-trend-up'
      }
      
      if (isRed('up_limit_buy_amount') && isRed('auction_pct') && isRed('auction_amount')) {
          signals.push('1')
      }

      // Logic for 2
      let condition2 = false
      if (index + 2 < this.tradeDays.length) {
          const prevDay = this.tradeDays[index + 1]
          const prevPrevDay = this.tradeDays[index + 2]
          
          const prevDailyAmount = Number(this.getAuctionValue(stock, prevDay, 'daily_trade_amount'))
          const prevPrevDailyAmount = Number(this.getAuctionValue(stock, prevPrevDay, 'daily_trade_amount'))
          const currAuctionAmount = Number(this.getAuctionValue(stock, day, 'auction_amount'))
          const currAuctionPct = Number(this.getAuctionValue(stock, day, 'auction_pct'))
          
          if (!Number.isNaN(prevDailyAmount) && !Number.isNaN(prevPrevDailyAmount) && 
              !Number.isNaN(currAuctionAmount) && !Number.isNaN(currAuctionPct)) {
              
              const cond2a = prevDailyAmount > prevPrevDailyAmount && prevDailyAmount > 500000000
              const cond2b = prevDailyAmount > 0 && (currAuctionAmount / prevDailyAmount > 0.1)
              const cond2c = currAuctionPct > 5
              
              if (cond2a && cond2b && cond2c) {
                  condition2 = true
              }
          }
      }
      
      if (condition2) signals.push('2')
          
          // Logic for 3
          let condition3 = false
          if (index + 1 < this.tradeDays.length) {
              const prevDay = this.tradeDays[index + 1]
              
              const currUpLimitBuy = Number(this.getAuctionValue(stock, day, 'up_limit_buy_amount'))
              const prevUpLimitBuy = Number(this.getAuctionValue(stock, prevDay, 'up_limit_buy_amount'))
              
              const currAuctionAmount = Number(this.getAuctionValue(stock, day, 'auction_amount'))
              const prevAuctionAmount = Number(this.getAuctionValue(stock, prevDay, 'auction_amount'))
              
              const currAuctionPct = Number(this.getAuctionValue(stock, day, 'auction_pct'))
              const prevAuctionPct = Number(this.getAuctionValue(stock, prevDay, 'auction_pct'))
              
              if (!Number.isNaN(currUpLimitBuy) && !Number.isNaN(prevUpLimitBuy) &&
                  !Number.isNaN(currAuctionAmount) && !Number.isNaN(prevAuctionAmount) &&
                  !Number.isNaN(currAuctionPct) && !Number.isNaN(prevAuctionPct)) {
                  
                  const condA = (currUpLimitBuy > prevUpLimitBuy) || (currAuctionAmount > prevAuctionAmount)
                  const condB = currAuctionPct > prevAuctionPct
                  
                  if (condA && condB) {
                      condition3 = true
                  }
              }
          }
          if (condition3) signals.push('3')

          if (signals.length === 0) return 0
      
      return signals.join(',')
    },
    showSidebar(stock) {
      this.sidebar_symbol_code = stock.stock_code
      this.sidebar_symbol_name = stock.stock_name
      this.sidebar_date_mark = this.date1
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
    }
  }
}
</script>

<style scoped>
.review-dingpan .table {
  font-size: 12px;
}
.stock-table {
  font-size: 12px;
  width: 100%;
}
.stock-table th, .stock-table td {
  padding: 1px 2px !important;
  vertical-align: middle;
  height: auto;
  text-align: center;
}
.dingpan-grid .row {
  margin-right: 0;
  margin-left: 0;
}
.auction-stock {
  background: var(--bg-card);
}
.bg-trend-up {
  background-color: #ffeaea !important;
}
.bg-trend-blue {
  background-color: #e6f7ff !important;
}
.bg-trend-red {
  background-color: #ffeaea !important;
}
.bg-signal-active {
  background-color: #ff0000 !important;
  color: #fff !important;
}
</style>
